import { useState } from 'react';
import { COMPANY_MAILBOX_KIND_LABEL_KEYS, fill, Size, t, type DocumentDeliveryView, type SendMailbox, type StaleSendWarning } from '@bms2/shared';
import { TextInput, Textarea } from '../../components/controls';
import { Modal } from '../../components/modals/Modal';
import modalStyles from '../../components/modals/modal.module.css';
import { DeliveredPanel } from './DeliveredPanel';
import { StaleSendBanner } from './StaleSendWarning';

/** What the window hands its Send: the typed address, which wins over the rule, and the person's own words. */
export interface DocumentSendFields {
  to: string | null;
  message: string | null;
}

export interface DocumentSendWindowProps {
  title: string;
  dataModal: string;
  /** The primary's label where no warning stands: the document's own Send. */
  sendLabel: string;
  /** What the press does beyond the mail, then who it goes to when To is left blank; one paragraph each. */
  hints: readonly string[];
  /** 🔒 The stale first-send warning (decision 1227); with it, the primary reads Send Anyway and the press says so. */
  stale: StaleSendWarning | null;
  /** 🔒 The last send's Delivered panel, drawn beneath the fields (decision 1226); null where nothing was mailed. */
  previousDelivery: DocumentDeliveryView | null | undefined;
  timeZone: string;
  busy: boolean;
  onSend: (fields: DocumentSendFields, sendAnyway: boolean) => void;
  onCancel: () => void;
  onChangeDate: () => void;
  /**
   * 🔒 The box the server chose for this document (decision 2974), read from the
   * document's own answer on the Organization's day: To opens on it, shown and
   * changeable. Null, To opens empty as before. The window lists no mailboxes and
   * works out no day of its own, so it agrees with the mail whoever opens it.
   */
  mailbox?: SendMailbox | null;
}

/**
 * 🔒 The Sales Order's and the Invoice's send window (piece 22; decision 2630),
 * the Quote's window over these two documents: To, which wins over the rule,
 * the person's message beneath the covering sentence, the stale first-send
 * warning with Send Anyway, and the last send's Delivered panel.
 *
 * 🔨 The window is mounted only while it is open, so the typed words start
 * empty on every open and never carry to another document.
 */
export function DocumentSendWindow({ title, dataModal, sendLabel, hints, stale, previousDelivery, timeZone, busy, onSend, onCancel, onChangeDate, mailbox }: DocumentSendWindowProps) {
  // 🔒 Decision 2974: To stands on the mailbox the server chose until the person types over it.
  const box = mailbox ?? null;
  const [typed, setTyped] = useState<string | null>(null);
  const to = typed ?? box?.email ?? '';
  const [message, setMessage] = useState('');
  const send = () => onSend({ to: to.trim() || null, message: message.trim() || null }, stale !== null);
  return (
    <Modal
      open
      title={title}
      onCancel={onCancel}
      dataModal={dataModal}
      actions={[
        { kind: 'primary', label: stale ? t('document.staleSend.sendAnyway') : sendLabel, onAct: send, disabled: busy },
        { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel },
      ]}
    >
      <StaleSendBanner warning={stale} onChangeDate={onChangeDate} />
      {hints.map((hint) => (
        <p key={hint} className={modalStyles.fieldLabel}>
          {hint}
        </p>
      ))}
      {typed === null && box ? (
        <p className={modalStyles.fieldLabel} data-send-mailbox={box.kind}>
          {fill(t('mailbox.send.filled'), { kind: t(COMPANY_MAILBOX_KIND_LABEL_KEYS[box.kind]) })}
        </p>
      ) : null}
      <TextInput size={Size.Medium} label={t('document.send.to')} value={to} onChange={setTyped} type="email" name={`${dataModal}-to`} />
      <Textarea size={Size.Large} label={t('document.send.message')} value={message} onChange={setMessage} />
      <DeliveredPanel delivery={previousDelivery} timeZone={timeZone} heading={t('document.delivery.previousTitle')} />
    </Modal>
  );
}
